import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS - ERC1155 NFT Trading Market Design
const PRIMARY = {
  lighter: '#e0f2fe',
  light: '#4dd0e1',
  main: '#00bcd4',
  dark: '#0097a7',
  darker: '#006064',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#fff3e0',
  light: '#ffb74d',
  main: '#ff9800',
  dark: '#f57c00',
  darker: '#e65100',
  contrastText: '#fff',
};

const ACCENT = {
  lighter: '#f3e5f5',
  light: '#ba68c8',
  main: '#9c27b0',
  dark: '#7b1fa2',
  darker: '#4a148c',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#e1f5fe',
  light: '#4fc3f7',
  main: '#29b6f6',
  dark: '#0288d1',
  darker: '#01579b',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#e8f5e8',
  light: '#81c784',
  main: '#4caf50',
  dark: '#388e3c',
  darker: '#1b5e20',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#fff8e1',
  light: '#ffcc02',
  main: '#ffc107',
  dark: '#ff8f00',
  darker: '#ff6f00',
  contrastText: '#000',
};

const ERROR = {
  lighter: '#ffebee',
  light: '#e57373',
  main: '#f44336',
  dark: '#d32f2f',
  darker: '#b71c1c',
  contrastText: '#fff',
};

const GREY = {
  0: '#FFFFFF',
  100: '#f8fafc',
  200: '#f1f5f9',
  300: '#e2e8f0',
  400: '#cbd5e1',
  500: '#94a3b8',
  600: '#64748b',
  700: '#475569',
  800: '#334155',
  900: '#1e293b',
  500_8: alpha('#94a3b8', 0.08),
  500_12: alpha('#94a3b8', 0.12),
  500_16: alpha('#94a3b8', 0.16),
  500_24: alpha('#94a3b8', 0.24),
  500_32: alpha('#94a3b8', 0.32),
  500_48: alpha('#94a3b8', 0.48),
  500_56: alpha('#94a3b8', 0.56),
  500_80: alpha('#94a3b8', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  secondary: createGradient(SECONDARY.light, SECONDARY.main),
  accent: createGradient(ACCENT.light, ACCENT.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  // ERC1155 NFT Market Brand Gradients
  nftBrand: `linear-gradient(135deg, ${PRIMARY.main}, ${ACCENT.main})`,
  nftBrandReverse: `linear-gradient(135deg, ${ACCENT.main}, ${PRIMARY.main})`,
  tradingGlow: `linear-gradient(135deg, ${PRIMARY.main}, ${SECONDARY.main}, ${ACCENT.main})`,
  marketPlace: `linear-gradient(45deg, ${PRIMARY.dark}, ${ACCENT.dark})`,
  digitalAsset: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.8)}, ${alpha(ACCENT.main, 0.8)})`,
  blockchain: `linear-gradient(90deg, ${PRIMARY.main}, ${INFO.main}, ${ACCENT.main})`,
  subtle: `linear-gradient(135deg, ${PRIMARY.lighter}, ${ACCENT.lighter})`,
};

const CHART_COLORS = {
  cyan: ['#00bcd4', '#26c6da', '#4dd0e1', '#80deea'],
  orange: ['#ff9800', '#ffb74d', '#ffcc02', '#ffe082'],
  purple: ['#9c27b0', '#ba68c8', '#ce93d8', '#e1bee7'],
  blue: ['#29b6f6', '#42a5f5', '#64b5f6', '#90caf9'],
  green: ['#4caf50', '#66bb6a', '#81c784', '#a5d6a7'],
  red: ['#f44336', '#ef5350', '#e57373', '#ef9a9a'],
};

const COMMON = {
  common: { black: '#0f172a', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  accent: { ...ACCENT, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: '#000' },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { 
      primary: GREY[800], 
      secondary: GREY[600], 
      disabled: GREY[500] 
    },
    background: { 
      paper: '#fff', 
      default: '#fafbfc', 
      neutral: GREY[100],
      // ERC1155 NFT Market Backgrounds
      nftCard: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.03)}, ${alpha(ACCENT.main, 0.03)})`,
      tradingPanel: `linear-gradient(135deg, ${alpha(PRIMARY.lighter, 0.5)}, ${alpha(ACCENT.lighter, 0.5)})`,
      marketplace: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.05)}, ${alpha(SECONDARY.main, 0.05)})`,
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { 
      primary: '#fff', 
      secondary: GREY[400], 
      disabled: GREY[600] 
    },
    background: { 
      paper: GREY[800], 
      default: GREY[900], 
      neutral: GREY[500_16],
      // ERC1155 NFT Market Dark Backgrounds
      nftCard: `linear-gradient(135deg, ${alpha(PRIMARY.dark, 0.1)}, ${alpha(ACCENT.dark, 0.1)})`,
      tradingPanel: `linear-gradient(135deg, ${alpha(PRIMARY.darker, 0.2)}, ${alpha(ACCENT.darker, 0.2)})`,
      marketplace: `linear-gradient(135deg, ${alpha(PRIMARY.dark, 0.15)}, ${alpha(SECONDARY.dark, 0.15)})`,
    },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;